import { useEffect, useState } from "react";
import { MessageCircle, Send, Heart } from "lucide-react";
import { getMessages, postMessage } from "@/lib/api";

export default function MessageWall() {
  const [messages, setMessages] = useState([]);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const data = await getMessages();
      setMessages(data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !text.trim()) {
      setError("Please add your name and a message.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const newMsg = await postMessage({ name, message: text });
      setMessages([newMsg, ...messages]);
      setText("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not post. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-ink-900/60 border border-gold/25 focus:border-gold/70 rounded-xl px-4 py-3 text-ivory placeholder:text-ivory-dim/60 outline-none transition-colors duration-300";

  return (
    <div data-testid="message-wall" className="grid lg:grid-cols-5 gap-8">
      {/* Composer */}
      <form
        onSubmit={onSubmit}
        data-testid="message-form"
        className="glass-card p-6 md:p-8 lg:col-span-2 self-start"
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center text-gold">
            <MessageCircle size={16} />
          </div>
          <div>
            <p className="text-[0.6rem] uppercase tracking-[0.4em] text-gold">
              Guestbook
            </p>
            <h3 className="font-serif text-2xl text-ivory">Leave a wish</h3>
          </div>
        </div>

        <input
          data-testid="message-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={60}
          placeholder="Your name"
          className={`${inputCls} mb-4`}
        />
        <textarea
          data-testid="message-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          maxLength={600}
          placeholder="A blessing, a memory, a tiny song lyric — leave a #PandA wish ❤️"
          className={`${inputCls} resize-none`}
        />
        <div className="flex items-center justify-between mt-4 gap-3">
          <span className="text-xs text-ivory-dim">{text.length}/600</span>
          <button
            type="submit"
            disabled={submitting}
            data-testid="message-submit"
            className="btn-gold disabled:opacity-60"
          >
            {submitting ? "Posting…" : "Post Wish"} <Send size={12} />
          </button>
        </div>
        {error && (
          <p data-testid="message-error" className="mt-3 text-sm text-burgundy-glow">
            {error}
          </p>
        )}
      </form>

      {/* Wall */}
      <div className="lg:col-span-3 space-y-5 max-h-[640px] overflow-y-auto pr-2 no-scrollbar">
        {loading && (
          <p data-testid="messages-loading" className="text-ivory-dim italic">
            Loading wishes…
          </p>
        )}
        {!loading && messages.length === 0 && (
          <div className="glass-card p-8 text-center" data-testid="messages-empty">
            <p className="font-serif italic text-xl text-ivory-muted">
              Be the first to leave a wish for #PandA ✨
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <article
            key={m.id}
            data-testid={`message-${i}`}
            className="glass-card p-5 md:p-6 group"
          >
            <header className="flex items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center text-gold-light text-sm font-serif italic">
                  {m.name.charAt(0).toUpperCase()}
                </div>
                <p className="font-serif text-lg text-ivory">{m.name}</p>
              </div>
              <Heart size={13} className="text-burgundy-glow opacity-70 group-hover:opacity-100 transition" fill="currentColor" />
            </header>
            <p className="text-ivory-muted leading-relaxed whitespace-pre-line">
              {m.message}
            </p>
            <p className="mt-3 text-[0.55rem] uppercase tracking-[0.4em] text-gold/70">
              #PandA · {new Date(m.created_at).toLocaleDateString()}
            </p>
          </article>
        ))}
      </div>
    </div>
  );
}
